import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { ChatRoomType, RoomMemberDto } from '../../../types/chat';
import { Avatar } from '../../../components/shared/Avatar';
import {
  Bell, ChevronLeft, ChevronRight, FileText, Megaphone, MessageCircle, Pencil, Search, UserPlus, X,
} from '../../../icons';
import { useAuthStore } from '../../../store/authStore';
import { useChatStore } from '../../../store/chatStore';
import { useChatDetailPanelStore } from '../../../store/chatDetailPanelStore';
import { useChatRoomRailStore } from '../../../store/chatRoomRailStore';
import { getChatLang, subscribeChatLang } from '../../../lib/chat-i18n';
import { setRoomMuted, updateRoomProfile, searchRoomMessages } from '../../../api/chat';
import { ensureMessageLoaded, waitForMessageEl } from './chat-search-jump';
import { InviteMembersModal } from './InviteMembersModal';
import { ChatMediaBrowserPanel } from './media/ChatMediaBrowserPanel';
import { PersonDetailPopup, type OrgPerson } from '../../../components/PersonDetailPopup';
import { findOrgPerson } from '../../../lib/org-person-lookup';
import { fetchPersonCard } from '../../../api/hr-people';
import { chatPlainPreview } from '../../../lib/chat-plain-preview';
// room-notice — 공지 팝업과 그 상태. 대화 위 띠(ChatNoticeBanner)와 같은 store 를 본다.
import { RoomNoticeDialog } from './RoomNoticeDialog';
import { useChatNoticeStore } from '../../../store/chatNoticeStore';
// chat-display-names — 내가 부르는 이름(사람·방). 1:1 방 이름도 여기로 바꾼다.
import { useChatDisplayNameStore, myNameFor } from '../../../store/chatDisplayNameStore';
// chat-room-styles — 대화방 꾸미기(배경·내 말풍선 색). 앱과 같은 카탈로그.
import { useChatRoomStyleStore, styleOf } from '../../../store/chatRoomStyleStore';
import { ROOM_BACKGROUNDS, ROOM_ACCENTS } from '../../../lib/chat-room-style';
import { Palette, BarChart3, Plus, Trash2 } from '../../../icons';
// chat-polls — 투표 만들기. 카드는 대화 안에서 그려진다(PollCard).
import { createPoll } from '../../../api/chat-polls';
import { formatPollDate } from '../../../lib/chat-poll-date';
import { useChatPollStore } from '../../../store/chatPollStore';
// chat-room-kick — 방장의 「내보내기」. 서버 라우트는 2026-09-16 부터 있었다.
import { KickMemberDialog } from './KickMemberDialog';
import { UserMinus } from '../../../icons';
import './ChatDetailPanel.css';

/**
 * ChatDetailPanel (chat-detail-panel — 2026-08-17 확정 시안) — 우측 상세보기 패널.
 *
 * 고정 메뉴바 왼쪽에 **도킹**된다(오버레이 아님 — 열면 중앙 메시지 폭이 줄어든다).
 *   상단: '정보' 제목 + 닫기 → 정보/파일 탭 → 액션 3개(각각 팝업)
 *   정보 탭: 방 프로필 카드(수정 가능) · 참여자 · 대화 콘텐츠 미리보기 · 만들기 목록
 *   파일 탭: 콘텐츠 상세(기존 ChatMediaBrowserPanel 재사용) + 뒤로가기
 * 닫으면 좁은 세로 바에 방 아이콘 + **인원 수 동그라미 배지**만 남고, 누르면 다시 열린다.
 * 아이콘에 마우스를 올리면 대화방명이 뜬다.
 *
 * 기존 ChatParticipantPanel 은 그대로 두고(회귀 0) ChatArea 의 렌더만 이 컴포넌트로 바꾼다.
 * 초대 모달과 미디어 브라우저는 있던 것을 그대로 쓴다 — 새로 만들지 않는다.
 */

interface ChatDetailPanelProps {
  roomId: string;
  roomType: ChatRoomType;
  roomName: string;
  roomDescription?: string | null;
  members: RoomMemberDto[];
  onMembersChanged: (members: RoomMemberDto[]) => void;
  /** 방 프로필 수정 성공 → 부모가 헤더/목록 표시를 갱신. */
  onRoomProfileChanged?: (patch: { name?: string; description?: string | null }) => void;
}

type Tab = 'info' | 'files';
type Dialog = 'invite' | 'find' | 'notif' | 'notice' | 'member-name' | 'style' | 'poll' | 'kick' | null;

/** 알림 범위 — 서버(`PUT /rooms/:id/mute`)는 켜기/끄기 두 가지만 안다. */
const NOTIF_OPTIONS = [
  { key: 'all', label: '모든 메시지', desc: '이 대화의 모든 메시지를 알립니다', muted: false, supported: true },
  { key: 'mention', label: '멘션만', desc: '서버가 아직 지원하지 않습니다', muted: false, supported: false },
  { key: 'off', label: '끄기', desc: '알리지 않습니다', muted: true, supported: true },
] as const;

export function ChatDetailPanel({
  roomId, roomType, roomName, roomDescription, members, onMembersChanged, onRoomProfileChanged,
}: ChatDetailPanelProps) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  const myId = useAuthStore((s) => s.user?.id?.toString() ?? '');
  // 기본은 닫힘. 옛 참가자 바와 저장 키를 나눠 그쪽 설정이 새어들지 않게 한다.
  const open = useChatDetailPanelStore((s) => s.open);
  const toggle = useChatDetailPanelStore((s) => s.toggle);

  const [tab, setTab] = useState<Tab>('info');
  const [dialog, setDialog] = useState<Dialog>(null);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(roomName);
  const [draftDesc, setDraftDesc] = useState(roomDescription ?? '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notif, setNotif] = useState<string>('all');
  // 내가 부르는 이름 — 공용 이름과 **따로** 둔다. 1:1 은 공용 이름을 쓸 수 없다(상대 화면까지 바뀐다).
  const displayNames = useChatDisplayNameStore((st) => st.names);
  const renameDisplay = useChatDisplayNameStore((st) => st.rename);
  const myRoomName = myNameFor(displayNames, 'room', roomId);
  const [draftMyName, setDraftMyName] = useState('');
  /** 이름을 고칠 참여자 — 사람 이름도 방 이름과 같은 구조로 내 쪽에만 붙는다. */
  const [renameTarget, setRenameTarget] = useState<RoomMemberDto | null>(null);
  const [draftMemberName, setDraftMemberName] = useState('');
  const roomStyles = useChatRoomStyleStore((st) => st.styles);
  const saveRoomStyle = useChatRoomStyleStore((st) => st.save);
  const myStyle = styleOf(roomStyles, roomId);
  const [styleError, setStyleError] = useState<string | null>(null);
  // 투표 만들기 초안 — 팝업을 닫으면 버린다(반쯤 쓴 투표가 남아 있으면 더 헷갈린다).
  const upsertPoll = useChatPollStore((st) => st.upsert);
  const [pollQ, setPollQ] = useState('');
  const [pollOpts, setPollOpts] = useState<string[]>(['', '']);
  /*
   * 무엇을 고르는 투표인가 (2026-08-26).
   *
   * 날짜를 묻는 투표가 가장 흔한데(회식·회의), 손으로 "9월 4일" 을 적으면 사람마다 다르게 적어
   * 같은 날이 두 항목이 된다. 종류를 '일정' 으로 두면 항목 칸이 달력이 되고, 고른 날짜를
   * **사람이 읽는 한 가지 표기**로 바꿔 담는다.
   *
   * 서버는 종류를 모른다 — 항목은 어차피 글자라 저장 형태가 같기 때문이다(스키마 변경 0).
   */
  const [pollKind, setPollKind] = useState<'text' | 'date'>('text');
  const [pollMulti, setPollMulti] = useState(false);
  const [pollAnon, setPollAnon] = useState(true);
  const [pollDeadline, setPollDeadline] = useState('');
  const [pollBusy, setPollBusy] = useState(false);
  const [pollError, setPollError] = useState<string | null>(null);

  const openPoll = useCallback(() => {
    setPollQ(''); setPollOpts(['', '']); setPollKind('text'); setPollMulti(false); setPollAnon(true);
    setPollDeadline(''); setPollError(null);
    setDialog('poll');
  }, []);

  const submitPoll = useCallback(async () => {
    const question = pollQ.trim();
    // 일정 투표는 고른 날짜를 한 가지 표기로 바꿔 담는다 — 같은 날이 두 항목이 되지 않게.
    const options = pollOpts
      .map((o) => (pollKind === 'date' ? formatPollDate(o.trim()) : o.trim()))
      .filter(Boolean);
    if (!question) { setPollError('무엇을 물어볼지 적어 주세요.'); return; }
    if (options.length < 2) {
      setPollError(pollKind === 'date' ? '날짜를 두 개 이상 골라 주세요.' : '항목을 두 개 이상 적어 주세요.');
      return;
    }
    if (new Set(options).size !== options.length) {
      setPollError(pollKind === 'date' ? '같은 날짜가 두 번 있습니다.' : '같은 항목이 두 번 있습니다.');
      return;
    }
    setPollBusy(true);
    setPollError(null);
    try {
      const poll = await createPoll(roomId, {
        question,
        options,
        multi: pollMulti,
        anonymous: pollAnon,
        // 입력은 그 사람의 시계 기준이라 ISO 로 바꿔 보낸다.
        closesAt: pollDeadline ? new Date(pollDeadline).toISOString() : null,
      });
      upsertPoll(poll);
      setDialog(null);
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { message?: string } } })?.response;
      setPollError(res?.status === 404
        ? '이 서버는 아직 투표를 지원하지 않습니다.'
        : res?.data?.message ?? '투표를 올리지 못했습니다.');
    } finally {
      setPollBusy(false);
    }
  }, [pollQ, pollOpts, pollKind, pollMulti, pollAnon, pollDeadline, roomId, upsertPoll]);
  const [findQ, setFindQ] = useState('');
  /** 참여자를 누르면 조직도와 **같은 팝업**을 띄운다. 조직 정보는 눌렀을 때 찾는다. */
  const [person, setPerson] = useState<OrgPerson | null>(null);

  // 공용 이름(모두에게 보이는 이름)은 1:1 에서 못 바꾼다 — 참여자가 둘뿐이라 상대 화면의
  // 이름까지 바뀌기 때문이고, 서버도 그래서 막는다(DIRECT_ROOM_IMMUTABLE).
  // 대신 **내가 부르는 이름**은 어떤 방이든 바꿀 수 있다 — 그래서 편집 자체는 항상 연다.
  const canEditShared = roomType !== 'direct';

  // room-notice — 공지 등록은 **방장만**(서버가 OWNER 외 403). 방장이 아니면 등록된 공지를
  // 읽기만 하고, 공지가 없으면 할 수 있는 일이 없으므로 줄 자체를 내지 않는다.
  const notice = useChatNoticeStore((s) => s.noticeByRoom[roomId]);
  const isOwner = members.some((m) => m.userId === myId && m.role === 'OWNER');
  const showNoticeRow = isOwner || !!notice;

  // chat-room-kick — 서버와 같은 경계: 방장만 · 1:1 아님 · 나 아님 · 상대가 방장 아님.
  const canKick = isOwner && roomType !== 'direct';
  const [kickTarget, setKickTarget] = useState<RoomMemberDto | null>(null);

  const openEdit = useCallback(() => {
    setDraftMyName(myRoomName ?? '');
    setDraftName(roomName);
    setDraftDesc(roomDescription ?? '');
    setSaveError(null);
    setEditing(true);
  }, [roomName, roomDescription, myRoomName]);

  const save = useCallback(async () => {
    const name = draftName.trim();
    if (canEditShared && !name) { setSaveError('방 이름은 비울 수 없습니다.'); return; }
    setSaving(true);
    setSaveError(null);
    try {
      // 내가 부르는 이름은 어떤 방이든 저장한다. 비우면 원래 이름으로 되돌아간다.
      if ((draftMyName.trim() || '') !== (myRoomName ?? '')) {
        await renameDisplay('room', roomId, draftMyName);
      }
      // 공용 이름·설명은 바꿀 수 있는 방에서만. 1:1 은 서버가 400 을 주므로 아예 보내지 않는다.
      if (canEditShared) {
        const next = await updateRoomProfile(roomId, { name, description: draftDesc.trim() });
        onRoomProfileChanged?.({ name: next.name ?? name, description: next.description });
      }
      setEditing(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : '저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  }, [draftName, draftDesc, draftMyName, myRoomName, canEditShared, renameDisplay, roomId, onRoomProfileChanged]);

  const pickNotif = useCallback(async (key: string) => {
    const opt = NOTIF_OPTIONS.find((o) => o.key === key);
    if (!opt || !opt.supported) return;
    setNotif(key);
    try { await setRoomMuted(roomId, opt.muted); } catch { /* 표시는 유지 — 다음 조회에서 교정 */ }
  }, [roomId]);

  // 대화 검색 — 종전 경로(폴백): 서버 검색이 없거나 실패하면 **이미 불러온 메시지**만 훑는다.
  const loaded = useChatStore((s) => s.messagesByRoom[roomId]);
  const localHits = useMemo(() => {
    const q = findQ.trim();
    if (!q || !loaded) return [];
    const needle = q.toLowerCase();
    return loaded
      .filter((m) => typeof m.content === 'string')
      // 찾는 것도 보여주는 것도 **사람이 읽는 글자**로 — AI 답변은 표가 html 블록이라
      // 날것으로 두면 `<table style=…>` 이 결과에 그대로 보이고 태그가 검색에도 걸린다.
      .map((m) => ({ msg: m, text: chatPlainPreview(m.content) }))
      .filter((h) => h.text.toLowerCase().includes(needle))
      .slice(-30)
      .reverse();
  }, [findQ, loaded]);

  /*
   * ★ 방 **전체** 검색 (chat-message-search — 2026-09-17).
   *   서버가 «재현율»(방 전체를 ILIKE 로 훑기), 화면이 «정밀도»(chatPlainPreview 로 마크업 제거 후
   *   글자 대조)를 맡는다. 거르는 규칙을 한 벌만 두려는 것이다 — 서버가 같은 정규식을 따로 가지면
   *   두 벌은 반드시 어긋난다.
   *   ⚠ 옛 서버는 이 라우트가 404 다. 그때는 `serverHits` 를 null 로 두어 위 종전 경로로 되돌아간다.
   */
  const [serverHits, setServerHits] = useState<typeof loaded | null>(null);
  const [serverMore, setServerMore] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = findQ.trim();
    if (dialog !== 'find' || q === '') { setServerHits(null); setServerMore(false); setSearching(false); return; }
    let alive = true;
    setSearching(true);
    // 글자마다 찌르지 않는다 — 입력이 멈춘 뒤에 한 번.
    const t = setTimeout(async () => {
      try {
        const res = await searchRoomMessages(roomId, { q, limit: 100 });
        if (!alive) return;
        setServerHits(res.messages);
        setServerMore(res.nextCursor != null);
      } catch {
        if (!alive) return;
        setServerHits(null);  // 옛 서버·실패 → 종전의 「불러온 것만 훑기」로
        setServerMore(false);
      } finally {
        if (alive) setSearching(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [dialog, findQ, roomId]);

  const findHits = useMemo(() => {
    const q = findQ.trim();
    if (!q || !serverHits) return localHits;
    const needle = q.toLowerCase();
    // 서버는 commit_seq 내림차순(최신 먼저)으로 준다 — 종전 경로의 `.slice(-30).reverse()` 와 달리
    // 여기서는 앞에서부터 자른다.
    return serverHits
      .filter((m) => typeof m.content === 'string')
      .map((m) => ({ msg: m, text: chatPlainPreview(m.content) }))
      .filter((h) => h.text.toLowerCase().includes(needle))
      .slice(0, 30);
  }, [findQ, serverHits, localHits]);

  const [jumping, setJumping] = useState(false);
  const jumpTo = useCallback(async (messageId: string) => {
    // 앵커는 메시지 행의 data-message-id (MessageItem·AiTextMessage).
    const sel = `[data-message-id="${CSS.escape(messageId)}"]`;
    let el = document.querySelector(sel);
    if (!el) {
      /*
       * ★ 방 전체 검색 결과에는 **아직 안 불러온 메시지**가 들어 있다. 종전처럼 그냥 두면
       *   창만 닫히고 아무 일도 안 일어나 «고장» 처럼 보인다. 과거 쪽으로 차례대로 불러온다.
       */
      setJumping(true);
      const ok = await ensureMessageLoaded(roomId, messageId);
      el = ok ? await waitForMessageEl(messageId) : null;
      setJumping(false);
      if (!el) return;  // 못 찾았으면 창을 닫지 않는다 — 결과 목록이 그대로 남는다
    }
    setDialog(null);
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('chat-detail-hit');
    setTimeout(() => el?.classList.remove('chat-detail-hit'), 2000);
  }, [roomId]);

  const openPerson = useCallback(async (m: RoomMemberDto) => {
    /**
     * ★★ **사람 하나는 사람 하나 라우트로** (2026-08-28 · 자리 재정의 판 3).
     *    지금까지는 단건 경로가 없어 `findOrgPerson` 이 **조직 트리를 통째로 읽어** 색인을
     *    만들었다. `GET /hr/people/:id` 가 그것을 대신한다 — 한 사람만 읽고, 무엇을 보여 줄지
     *    서버가 범위로 정하며, 남의 것을 연 것은 기록에 남는다.
     * ★ 옛 서버(404)에서는 **종전 길로 되돌아간다** — 프론트를 먼저 배포해도 안 깨진다.
     */
    try {
      const { person: p } = await fetchPersonCard(m.userId);
      setPerson({
        id: p.userId, name: p.name, email: p.email, position: p.position,
        role: m.role ?? 'STAFF', status: p.status, isActive: true,
        phone: p.phone ?? null, employeeNumber: p.employeeNumber ?? null,
        avatarUrl: p.avatarUrl ?? m.avatarUrl ?? null,
        divisionName: p.divisionName, teamName: p.teamName,
        duty: p.isLeader ? '팀장' : null,
      });
      return;
    } catch { /* 옛 서버 · 실패 — 아래 종전 길로 */ }

    // 조직에서 찾으면 부서·팀까지, 못 찾으면 채팅이 아는 것만으로 같은 모습을 그린다.
    const found = await findOrgPerson(m.userId);
    setPerson(found ?? {
      id: m.userId, name: m.name, email: '', position: m.jobTitle ?? null,
      role: m.role ?? 'STAFF', status: 'offline', isActive: true,
      phone: null, employeeNumber: null, avatarUrl: m.avatarUrl ?? null,
      divisionName: null, teamName: null, duty: null,
    });
  }, []);

  const memberIds = useMemo(() => members.map((m) => m.userId), [members]);
  /** 메시지는 senderId 만 싣는다 — 이름은 멤버 목록에서 찾는다(MessageList 와 같은 방식). */
  const nameOf = useCallback(
    (senderId: string | null) => members.find((m) => m.userId === senderId)?.name ?? '',
    [members],
  );
  const shown = members.slice(0, 5);
  const rest = members.length - shown.length;

  // 방 카드는 **우측 고정 메뉴바의 메모 아래**에 놓인다(전역 레일이 그린다).
  // 여기서는 그 자리에 무엇을 놓을지만 올려두고, 접혔을 때는 아무것도 그리지 않는다.
  const publishRail = useChatRoomRailStore((st) => st.publish);
  const clearRail = useChatRoomRailStore((st) => st.clear);
  useEffect(() => {
    publishRail({ roomId, name: roomName, memberCount: members.length }, open, toggle);
  }, [publishRail, roomId, roomName, members.length, open, toggle]);
  // 채팅 화면을 벗어나면 비운다 — 다른 화면의 레일에 남으면 거짓말이 된다.
  useEffect(() => () => clearRail(), [clearRail]);

  if (!open) return null;

  /* ── 열림: 도킹 패널 ─────────────────────────────────────────────── */
  return (
    <aside className="chat-detail-panel" aria-label={tab === 'files' ? '콘텐츠' : '정보'}>
      {tab === 'files' ? (
        <>
          <div className="chat-detail-head">
            <button type="button" className="chat-detail-iconbtn" aria-label="뒤로가기" onClick={() => setTab('info')}>
              <ChevronLeft size={16} />
            </button>
            <span className="chat-detail-title">콘텐츠</span>
            <button type="button" className="chat-detail-iconbtn chat-detail-close" aria-label="닫기" onClick={toggle}>
              <X size={15} />
            </button>
          </div>
          <div className="chat-detail-body">
            <ChatMediaBrowserPanel roomId={roomId} members={members} />
          </div>
        </>
      ) : (
        <>
          <div className="chat-detail-head">
            <span className="chat-detail-title">정보</span>
            <button type="button" className="chat-detail-iconbtn chat-detail-close" aria-label="닫기" onClick={toggle}>
              <X size={15} />
            </button>
          </div>

          <div className="chat-detail-tabs" role="tablist">
            <button type="button" className="chat-detail-tab" role="tab" aria-selected onClick={() => setTab('info')}>정보</button>
            <button type="button" className="chat-detail-tab" role="tab" aria-selected={false} onClick={() => setTab('files')}>파일</button>
          </div>

          <div className="chat-detail-acts">
            <button type="button" className="chat-detail-act" aria-haspopup="dialog" onClick={() => setDialog('invite')}>
              <UserPlus size={17} />멤버 초대
            </button>
            <button type="button" className="chat-detail-act" aria-haspopup="dialog" onClick={() => { setFindQ(''); setDialog('find'); }}>
              <Search size={16} />대화 검색
            </button>
            <button type="button" className="chat-detail-act" aria-haspopup="dialog" onClick={() => setDialog('notif')}>
              <Bell size={17} />알림 설정
            </button>
          </div>

          <div className="chat-detail-body">
            {editing ? (
              <div className="chat-detail-edit">
                <label className="chat-detail-fld">
                  <span>내가 부르는 이름</span>
                  <input
                    type="text" value={draftMyName} maxLength={100} autoComplete="off"
                    placeholder={roomName}
                    onChange={(e) => setDraftMyName(e.target.value)}
                  />
                </label>
                <p className="chat-detail-fldnote">이 이름은 나에게만 보입니다. 비우면 원래 이름으로 돌아갑니다.</p>
                {canEditShared ? (
                  <>
                    <label className="chat-detail-fld">
                      <span>모두에게 보이는 이름</span>
                      <input
                        type="text" value={draftName} maxLength={100} autoComplete="off"
                        onChange={(e) => setDraftName(e.target.value)}
                      />
                    </label>
                    <label className="chat-detail-fld">
                      <span>설명</span>
                      <input
                        type="text" value={draftDesc} maxLength={500} autoComplete="off"
                        onChange={(e) => setDraftDesc(e.target.value)}
                      />
                    </label>
                  </>
                ) : (
                  <p className="chat-detail-fldnote">
                    1:1 대화는 참여자가 둘뿐이라, 모두에게 보이는 이름을 바꾸면 상대 화면의 이름도 바뀝니다.
                    그래서 여기서는 내가 부르는 이름만 정합니다.
                  </p>
                )}
                {saveError && <p className="chat-detail-err" role="alert">{saveError}</p>}
                <div className="chat-detail-editfoot">
                  <button type="button" className="chat-detail-btn" onClick={() => setEditing(false)} disabled={saving}>취소</button>
                  <button type="button" className="chat-detail-btn is-primary" onClick={save} disabled={saving}>
                    {saving ? '저장 중…' : '저장'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="chat-detail-card-box">
                <button type="button" className="chat-detail-iconbtn chat-detail-edit-btn" aria-label="채팅방 정보 수정" onClick={openEdit}>
                  <Pencil size={15} />
                </button>
                <span className="chat-detail-ico is-lg" aria-hidden>{roomName.trim().charAt(0) || '#'}</span>
                <span className="chat-detail-name">{roomName}</span>
                {roomDescription && <span className="chat-detail-desc">{roomDescription}</span>}
                <span className="chat-detail-avatars">
                  {shown.map((m) => (
                    <Avatar key={m.userId} userId={Number(m.userId) || 0} name={m.name} size="xs" />
                  ))}
                  {rest > 0 && <span className="chat-detail-more">+{rest}</span>}
                </span>
              </div>
            )}

            <ul className="chat-detail-members">
              {members.map((m) => {
                // 내가 지은 이름이 있으면 그것으로 부르고, 원래 이름은 아래 줄에 함께 둔다
                // (누구인지 헷갈리지 않게).
                const mine = myNameFor(displayNames, 'user', m.userId);
                const shownName = mine ?? m.name;
                const sub = mine ? `${m.name}${m.jobTitle ? ` · ${m.jobTitle}` : ''}` : m.jobTitle;
                return (
                  <li key={m.userId} className="chat-detail-memberli">
                    <button
                      type="button"
                      className="chat-detail-member"
                      aria-label={`${shownName} 정보`}
                      onClick={() => void openPerson(m)}
                    >
                      <Avatar userId={Number(m.userId) || 0} name={m.name} size="xs" />
                      <span className="chat-detail-mtext">
                        <span className="chat-detail-mname">
                          {shownName}{m.userId === myId && <span className="chat-detail-me">나</span>}
                        </span>
                        {sub && <span className="chat-detail-msub">{sub}</span>}
                      </span>
                    </button>
                    {/* 버튼 안에 버튼을 넣으면 파서가 바깥을 먼저 닫는다 — 형제로 두고 겹쳐 놓는다. */}
                    {m.userId !== myId && (
                      <button
                        type="button"
                        className="chat-detail-memberedit"
                        aria-label={`${m.name} 이름 바꾸기`}
                        onClick={() => {
                          setRenameTarget(m);
                          setDraftMemberName(mine ?? '');
                          setDialog('member-name');
                        }}
                      >
                        <Pencil size={12} />
                      </button>
                    )}
                    {canKick && m.userId !== myId && m.role !== 'OWNER' && (
                      <button
                        type="button"
                        className="chat-detail-memberedit chat-detail-memberkick"
                        aria-label={`${m.name} 내보내기`}
                        title="내보내기"
                        onClick={() => { setKickTarget(m); setDialog('kick'); }}
                      >
                        <UserMinus size={12} />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="chat-detail-sec">
              대화 콘텐츠
              <button type="button" className="chat-detail-link" onClick={() => setTab('files')}>전체 보기</button>
            </div>
            <div className="chat-detail-make">
              <button type="button" onClick={() => setTab('files')}>
                <span className="chat-detail-mkico"><FileText size={15} /></span>
                <span className="chat-detail-mkl">파일 · 사진 · 링크 보기</span>
                <ChevronRight size={14} />
              </button>
              <button type="button" onClick={() => setDialog('find')}>
                <span className="chat-detail-mkico"><MessageCircle size={15} /></span>
                <span className="chat-detail-mkl">대화에서 찾기</span>
                <ChevronRight size={14} />
              </button>

              {/* 선 하나로 '보는 것'과 '꾸미고 만드는 것'을 가른다(확정 시안). 투표는 후속. */}
              <div className="chat-detail-mkdiv" role="presentation" />

              {/* chat-room-styles — 내 화면의 이 방 모습. 방장 권한과 무관하게 각자 고른다. */}
              <button type="button" onClick={() => { setStyleError(null); setDialog('style'); }}>
                <span className="chat-detail-mkico"><Palette size={15} /></span>
                <span className="chat-detail-mkl">대화방 꾸미기</span>
                <ChevronRight size={14} />
              </button>

              {/* room-notice — 방장은 등록, 그 밖은 공지가 있을 때만 보기. */}
              {showNoticeRow && (
                <>
                  <button type="button" onClick={() => setDialog('notice')}>
                    <span className="chat-detail-mkico"><Megaphone size={15} /></span>
                    <span className="chat-detail-mkl">{isOwner ? '공지 등록' : '공지 보기'}</span>
                    {notice && <span className="chat-detail-mktag">등록됨</span>}
                    <ChevronRight size={14} />
                  </button>
                </>
              )}

              {/* chat-polls — 누구나 만들 수 있다. 마감은 만든 사람과 방장만. */}
              <button type="button" onClick={openPoll}>
                <span className="chat-detail-mkico"><BarChart3 size={15} /></span>
                <span className="chat-detail-mkl">투표 만들기</span>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </>
      )}

      {person && (
        <PersonDetailPopup
          person={person}
          onClose={() => setPerson(null)}
          onNavigated={() => setPerson(null)}
        />
      )}

      {dialog === 'invite' && (
        <InviteMembersModal
          roomId={roomId}
          existingMemberIds={memberIds}
          onClose={() => setDialog(null)}
          onInvited={(next) => { onMembersChanged(next); setDialog(null); }}
        />
      )}

      {dialog === 'poll' && (
        <div
          className="chat-detail-scrim"
          role="presentation"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !pollBusy) setDialog(null); }}
        >
          <div className="chat-detail-dlg" role="dialog" aria-modal="true" aria-label="투표 만들기">
            <div className="chat-detail-dlghead">
              <span className="chat-detail-title">투표 만들기</span>
              <button type="button" className="chat-detail-iconbtn chat-detail-close" aria-label="닫기" onClick={() => setDialog(null)}>
                <X size={15} />
              </button>
            </div>
            <div className="chat-detail-dlgbody">
              <label className="chat-detail-fld">
                <span>무엇을 물어볼까요 <span className="chat-poll-count">{pollQ.length} / 100</span></span>
                <input
                  type="text" value={pollQ} maxLength={100} autoComplete="off"
                  placeholder="예: 회식 날짜 정합시다"
                  onChange={(e) => setPollQ(e.target.value)}
                />
              </label>

              {/* 무엇을 고르는 투표인가 — 일정이면 항목 칸이 달력이 된다. */}
              <div className="chat-detail-fld">
                <span>무엇을 고르나</span>
                <div className="chat-poll-kind" role="radiogroup" aria-label="투표 종류">
                  {([['text', '텍스트'], ['date', '일정']] as const).map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      role="radio"
                      aria-checked={pollKind === k}
                      className={pollKind === k ? 'is-on' : ''}
                      onClick={() => {
                        if (pollKind === k) return;
                        setPollKind(k);
                        // 종류가 바뀌면 적어 둔 항목은 비운다 — 날짜 칸에 글자가 남으면
                        // 달력이 그 값을 못 읽어 빈 칸처럼 보인다.
                        setPollOpts(['', '']);
                        setPollError(null);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="chat-detail-fld">
                <span>
                  {pollKind === 'date' ? '날짜' : '항목'}
                  {' '}
                  <span className="chat-poll-count">{pollOpts.length} / 10</span>
                </span>
                {pollOpts.map((v, i) => (
                  <div className="chat-poll-optrow" key={i}>
                    {/* 일정이면 달력 — 손으로 적게 두면 같은 날이 여러 표기로 갈린다. */}
                    <input
                      type={pollKind === 'date' ? 'date' : 'text'}
                      value={v}
                      maxLength={pollKind === 'date' ? undefined : 80}
                      autoComplete="off"
                      placeholder={pollKind === 'date' ? undefined : `항목 ${i + 1}`}
                      aria-label={pollKind === 'date' ? `날짜 ${i + 1}` : `항목 ${i + 1}`}
                      onChange={(e) => setPollOpts((cur) => cur.map((c, j) => (j === i ? e.target.value : c)))}
                    />
                    {/* 고른 날이 어떻게 보일지 그 자리에서 알려 준다. */}
                    {pollKind === 'date' && v.trim() && (
                      <span className="chat-poll-datehint">{formatPollDate(v)}</span>
                    )}
                    {/* 두 개는 있어야 투표가 된다 — 그 아래로는 지울 수 없다. */}
                    {pollOpts.length > 2 && (
                      <button
                        type="button"
                        className="chat-poll-optdel"
                        aria-label={`${pollKind === 'date' ? '날짜' : '항목'} ${i + 1} 지우기`}
                        onClick={() => setPollOpts((cur) => cur.filter((_, j) => j !== i))}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                ))}
                {pollOpts.length < 10 && (
                  <button
                    type="button"
                    className="chat-poll-optadd"
                    onClick={() => setPollOpts((cur) => [...cur, ''])}
                  >
                    <Plus size={13} /> {pollKind === 'date' ? '날짜 추가' : '항목 추가'}
                  </button>
                )}
              </div>

              <div className="chat-poll-switches">
                <label className="chat-poll-switch">
                  <input type="checkbox" checked={pollMulti} onChange={(e) => setPollMulti(e.target.checked)} />
                  <span><b>여러 개 고르기</b><span>참여자가 항목을 여러 개 고를 수 있습니다</span></span>
                </label>
                <label className="chat-poll-switch">
                  <input type="checkbox" checked={pollAnon} onChange={(e) => setPollAnon(e.target.checked)} />
                  <span><b>익명 투표</b><span>누가 무엇을 골랐는지 아무도 못 봅니다</span></span>
                </label>
                <label className="chat-poll-switch">
                  <span className="chat-poll-switchtxt"><b>마감 시각</b><span>비워 두면 마감 없이 계속 열립니다</span></span>
                  <input
                    type="datetime-local"
                    className="chat-poll-deadline"
                    value={pollDeadline}
                    aria-label="마감 시각"
                    onChange={(e) => setPollDeadline(e.target.value)}
                  />
                </label>
              </div>

              {pollError && <p className="chat-detail-err" role="alert">{pollError}</p>}

              <div className="chat-detail-editfoot">
                <button type="button" className="chat-detail-btn" onClick={() => setDialog(null)} disabled={pollBusy}>취소</button>
                <button type="button" className="chat-detail-btn is-primary" onClick={() => void submitPoll()} disabled={pollBusy}>
                  {pollBusy ? '올리는 중…' : '투표 올리기'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {dialog === 'style' && (
        <div
          className="chat-detail-scrim"
          role="presentation"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setDialog(null); }}
        >
          <div className="chat-detail-dlg" role="dialog" aria-modal="true" aria-label="대화방 꾸미기">
            <div className="chat-detail-dlghead">
              <span className="chat-detail-title">대화방 꾸미기</span>
              <button type="button" className="chat-detail-iconbtn chat-detail-close" aria-label="닫기" onClick={() => setDialog(null)}>
                <X size={15} />
              </button>
            </div>
            <div className="chat-detail-dlgbody">
              <p className="chat-detail-fldnote">이 방을 내 화면에서 어떻게 보일지 정합니다. 다른 사람 화면은 그대로입니다.</p>

              <div className="chat-detail-sec" style={{ padding: '10px 0 6px' }}>배경</div>
              <div className="chat-style-grid" role="radiogroup" aria-label="배경">
                {ROOM_BACKGROUNDS.map((b) => {
                  const on = (myStyle.backgroundId ?? 'none') === b.id;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      className={`chat-style-sw${on ? ' is-on' : ''}`}
                      style={b.gradient
                        ? { background: `linear-gradient(160deg, ${b.gradient[0]}, ${b.gradient[1]})` }
                        : undefined}
                      onClick={() => {
                        setStyleError(null);
                        void saveRoomStyle(roomId, { backgroundId: b.id })
                          .catch((err: unknown) => setStyleError(err instanceof Error ? err.message : '저장하지 못했습니다.'));
                      }}
                    >
                      <span className="chat-style-swlabel">{b.label}</span>
                    </button>
                  );
                })}
              </div>

              <div className="chat-detail-sec" style={{ padding: '14px 0 6px' }}>내 말풍선 색</div>
              <div className="chat-style-dots" role="radiogroup" aria-label="내 말풍선 색">
                {ROOM_ACCENTS.map((a) => {
                  const on = (myStyle.accentId ?? 'default') === a.id;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      aria-label={a.label}
                      title={a.label}
                      className={`chat-style-dot${on ? ' is-on' : ''}${a.hex ? '' : ' is-default'}`}
                      style={a.hex ? { background: a.hex } : undefined}
                      onClick={() => {
                        setStyleError(null);
                        void saveRoomStyle(roomId, { accentId: a.id })
                          .catch((err: unknown) => setStyleError(err instanceof Error ? err.message : '저장하지 못했습니다.'));
                      }}
                    />
                  );
                })}
              </div>

              {styleError && <p className="chat-detail-err" role="alert">{styleError}</p>}
              <p className="chat-detail-fldnote" style={{ marginTop: 12 }}>
                앱에서 고른 움직이는 배경은 PC 에서 같은 색의 정적 배경으로 열립니다.
              </p>
            </div>
          </div>
        </div>
      )}

      {dialog === 'member-name' && renameTarget && (
        <div
          className="chat-detail-scrim"
          role="presentation"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setDialog(null); }}
        >
          <div className="chat-detail-dlg is-sm" role="dialog" aria-modal="true" aria-label="이름 바꾸기">
            <div className="chat-detail-dlghead">
              <span className="chat-detail-title">이름 바꾸기</span>
              <button type="button" className="chat-detail-iconbtn chat-detail-close" aria-label="닫기" onClick={() => setDialog(null)}>
                <X size={15} />
              </button>
            </div>
            <div className="chat-detail-dlgbody">
              <label className="chat-detail-fld">
                <span>{renameTarget.name} 님을 부를 이름</span>
                <input
                  type="text" value={draftMemberName} maxLength={100} autoComplete="off"
                  placeholder={renameTarget.name}
                  onChange={(e) => setDraftMemberName(e.target.value)}
                />
              </label>
              <p className="chat-detail-fldnote">
                이 이름은 나에게만 보입니다. 비우면 원래 이름으로 돌아갑니다.
              </p>
              <div className="chat-detail-editfoot">
                <button type="button" className="chat-detail-btn" onClick={() => setDialog(null)}>취소</button>
                <button
                  type="button"
                  className="chat-detail-btn is-primary"
                  onClick={() => {
                    const target = renameTarget;
                    setDialog(null);
                    void renameDisplay('user', target.userId, draftMemberName);
                  }}
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {dialog === 'kick' && kickTarget && (
        <KickMemberDialog
          roomId={roomId}
          target={kickTarget}
          shownName={myNameFor(displayNames, 'user', kickTarget.userId) ?? kickTarget.name}
          onClose={() => setDialog(null)}
          onKicked={(userId) => {
            onMembersChanged(members.filter((x) => x.userId !== userId));
            setKickTarget(null);
            setDialog(null);
          }}
        />
      )}

      {dialog === 'notice' && (
        <RoomNoticeDialog roomId={roomId} canEdit={isOwner} onClose={() => setDialog(null)} />
      )}

      {dialog === 'find' && (
        <div className="chat-detail-scrim" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setDialog(null); }}>
          <div className="chat-detail-dlg" role="dialog" aria-modal="true" aria-label="대화 검색">
            <div className="chat-detail-dlghead">
              <span className="chat-detail-title">대화 검색</span>
              <button type="button" className="chat-detail-iconbtn chat-detail-close" aria-label="닫기" onClick={() => setDialog(null)}>
                <X size={15} />
              </button>
            </div>
            <div className="chat-detail-dlgbody">
              <label className="chat-detail-fbox">
                <Search size={16} />
                <input
                  type="search" value={findQ} placeholder="대화 내용 검색" aria-label="대화 검색"
                  autoComplete="off" onChange={(e) => setFindQ(e.target.value)}
                />
              </label>
              {!findQ.trim() ? (
                <p className="chat-detail-empty">찾을 말을 입력하세요.</p>
              ) : searching && findHits.length === 0 ? (
                <p className="chat-detail-empty">찾는 중…</p>
              ) : findHits.length === 0 ? (
                <p className="chat-detail-empty">일치하는 대화가 없습니다.</p>
              ) : (
                <ul className="chat-detail-hits">
                  {findHits.map(({ msg, text }) => (
                    <li key={msg.id}>
                      <button type="button" onClick={() => jumpTo(msg.id)}>
                        <span className="chat-detail-hitw">{msg.isAi ? 'AI 비서' : nameOf(msg.senderId)}</span>
                        <span className="chat-detail-hitt">{text}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {/*
                * 무엇을 뒤졌는지 사실대로 적는다 — 서버 검색이 되면 방 전체, 옛 서버로 폴백했으면
                * 종전대로 «불러온 것만». 같은 문구를 두면 폴백일 때 사용자가 「왜 안 나오지」를 겪는다.
                */}
              <p className="chat-detail-note">
                {jumping
                  ? '대화를 불러오는 중…'
                  : serverHits
                    ? (serverMore ? '이 방의 모든 대화에서 찾습니다 — 최근 것부터 보여 줍니다.' : '이 방의 모든 대화에서 찾습니다.')
                    : '불러온 대화 안에서만 찾습니다.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {dialog === 'notif' && (
        <div className="chat-detail-scrim" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setDialog(null); }}>
          <div className="chat-detail-dlg is-sm" role="dialog" aria-modal="true" aria-label="알림 설정">
            <div className="chat-detail-dlghead">
              <span className="chat-detail-title">알림 설정</span>
              <button type="button" className="chat-detail-iconbtn chat-detail-close" aria-label="닫기" onClick={() => setDialog(null)}>
                <X size={15} />
              </button>
            </div>
            <div className="chat-detail-dlgbody">
              <div className="chat-detail-radios" role="radiogroup" aria-label="알림 범위">
                {NOTIF_OPTIONS.map((o) => (
                  <label key={o.key} className={o.supported ? '' : 'is-off'}>
                    <input
                      type="radio" name="chat-detail-notif" value={o.key}
                      checked={notif === o.key} disabled={!o.supported}
                      onChange={() => pickNotif(o.key)}
                    />
                    <span>{o.label}<span className="chat-detail-rdsub">{o.desc}</span></span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
